/*
 *  Product Gallery
 *    Scripts for the product gallery
 *
 *  Version:
 *    1.0.0 - 2020/11/19
 */

import KeenSlider from 'keen-slider';
import { initCarousel, CarouselOptions } from './../../objects/carousel';
import {


  responsiveMediaAbove,
  responsiveMediaBelow,
  responsiveQuery,
} from '../../settings/responsive';
import { CLASS_HIDDEN, CLASS_INITIALISED } from '../../tools/constants/classes';
import { Product } from '../../tools/product/product';
import { getVariant } from '../../objects/product/variant-selector';
import { handlize, pictureGenerate } from '@process-creative/slate-theme-tools';
import { Variant } from '../../tools/product/variant';
import { ProductMedia } from '../../tools/product/product-media';
import { openModal, closeModal, onModalClose } from '../modal';

// Selectors
const SELECTOR_CONTAINER = '[data-product-gallery]';
const SELECTOR_CAROUSEL = '[data-product-gallery-slides]';
const SELECTOR_DOTS_CAROUSEL = '[data-carousel-dots]';
const SELECTOR_TEMPLATE = '[data-product-template]';
const SELECTOR_VARIANT_SELECTOR = '[data-variant-selector]';
const SELECTOR_HOVERZOOM = '[data-hoverzoom]';
const HOVERZOOM_ATTR = 'data-hoverzoom';

const CLASS_IS_SLIDER = 'is-dot-slider';
const DOT_LIMIT = 10; //How many dots to show per carousel

//Variables for the Custom LightBox/gallery Desktop
const SELECTOR_LIGHTBOX = '[data-modal-container]';
const SELECTOR_LB_CAROUSEL = '[data-product-lightbox-slides]';
const SELECTOR_MODAL_OPEN = '[data-modal-open]';
const SELECTOR_MODAL_OBJ = '[data-modal-object]';
const SELECTOR_CONTROLS = '[data-carousel-controls]';
const SELECTOR_LB_ZOOM = '[data-lightbox-zoom]';
const MODAL_ID = 'productModal';

// Initialise Carousel
document
  .querySelectorAll<HTMLElement>(SELECTOR_CONTAINER)
  .forEach((container) => {
    let currentVariant = getVariant();
    let carouselInitialised:{
      slider: KeenSlider;
      autoplay: (state?: boolean) => void;
      slides: HTMLElement[];
  } | undefined = undefined;
    const dotsContainer = container.querySelector<HTMLElement>(
      SELECTOR_DOTS_CAROUSEL
    );
    const controlsContainer =
      container.querySelector<HTMLElement>(SELECTOR_CONTROLS);
    const carouselElement =
      container.querySelector<HTMLElement>(SELECTOR_CAROUSEL);
    const template = document.querySelector<HTMLElement>(SELECTOR_TEMPLATE);
    let zoomStatus = false;

    if (!carouselElement || !template) return;

    //Update images in carousel when a swatch has been selected
    window.addEventListener('product-swatch-selected', (event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.variant) {
        currentVariant = customEvent.detail.variant;
        showVariantImagesFromSelectedVariant();
      } else {
        showVariantImagesFromSelectedSwatches(
          customEvent.detail?.colour || '',
          customEvent.detail?.type || ''
        );
      }
    });

    const options = {
      slidesPerView: 1,
      loop: false,
      limitDots: DOT_LIMIT,
      breakpoints: {
        [responsiveQuery('SMALL')]: {
          created: (slider: KeenSlider) => slider.destroy(),
        },
      },
    };

    let params = {
      container: carouselElement,
      options: options,
      dotsContainer: dotsContainer,
      controlsContainer: controlsContainer,
    };
    let carousel = initCarousel(params);

    //Product and option position data
    const productObj: Product = JSON.parse(
      template!
        .querySelector(SELECTOR_VARIANT_SELECTOR)!
        .getAttribute('data-product')!
    );
    const productMediaImages = productObj.media.filter(
      (m) => m.media_type === 'image'
    );
    const colourOptionPosition = Object.values(
      productObj.options_with_values
    ).reduce((x: number | null, option) => {
      if (x) return x;
      if (['color', 'colour'].includes(option!.name.toLocaleLowerCase())) {
        return option!.position;
      } else {
        return x;
      }
    }, null);

    const typeOptionPosition = Object.values(
      productObj.options_with_values
    ).reduce((x: number | null, option) => {
      if (x) return x;
      if (['type'].includes(option!.name.toLocaleLowerCase())) {
        return option!.position;
      } else {
        return x;
      }
    }, null);

    let currentColor = '';
    let currentType = '';

    const productHasColorImages = mediaHasColorValues();
    showVariantImagesFromSelectedVariant();

    const onResize = async () => {
      try {
        await debounce(100);
      } catch {
        return;
      }

      if (!carousel || !carouselElement) return;
      const slider = carousel.slider;

      if (
        responsiveMediaAbove('SMALL') &&
        carouselElement.classList.contains(CLASS_INITIALISED)
      ) {
        slider.destroy();
      } else if (
        responsiveMediaBelow('SMALL') &&
        !carouselElement.classList.contains(CLASS_INITIALISED)
      ) {
        slider.refresh();
      }
    };

    window.addEventListener('resize', onResize);

    let previousDebounceTimeout: any;
    let previousDebounceReject: any;
    function debounce(ms: number): Promise<void> {
      clearTimeout(previousDebounceTimeout);
      previousDebounceReject?.();
      return new Promise((resolve, reject) => {
        previousDebounceReject = reject;
        previousDebounceTimeout = setTimeout(resolve, ms);
      });
    }

    intialiseLightbox();

    const getAlt = (media: any) => {
      if (!media.alt) return false;
      let mainAlt = media.alt.split('|');
      let mediaAltarray = mainAlt[0].split(',');

      return mediaAltarray;
    };

    /**
     * Rebuild images based on the "colour" or "color" selected
     */
    const showVariantImagesFromSelectedSwatches: (
      colour: string,
      type: string
    ) => void = (colour = '', type = '') => {
      if (!productMediaImages) return;
      if (!productHasColorImages) return;
      let variantColour = colour;
      let variantType = type;

      if (!variantColour) variantColour = ''; //This way we can check if '' === ''.
      if (!variantType) variantType = ''; //This way we can check if '' === ''.
      if (currentColor === variantColour && currentType === variantType) return; //Don't update if it's the same
      currentColor = variantColour;
      currentType = variantType;

      const imagesForThisColour: ProductMedia[] = productMediaImages.filter(
        (media) => {
          const mediaAlt = getAlt(media);
          if (handlize(mediaAlt[0]) === variantColour?.toLowerCase()) {
            if (mediaAlt[1] === undefined) {
              return true;
            }
            if (handlize(mediaAlt[1]) === variantType?.toLowerCase()) {
              return true;
            }
          }
        }
      );

      // Return first colour variant images if nothing is selected on page load
      let firstImagesforCarousel: ProductMedia[] = [];
      if (imagesForThisColour.length == 0) {
        const firstColourAlt = getAlt(productMediaImages[0]);
        firstImagesforCarousel = productMediaImages.filter((media) => {
          const mediaAlt = getAlt(media);
          if (handlize(mediaAlt[0]) == handlize(firstColourAlt)) {
            return true;
          }
        });

        rebuildCarousel(firstImagesforCarousel);
      } else {
        rebuildCarousel(imagesForThisColour);
      }
    };

    /**
     * Rebuild images based on the "colour" or "color" selected
     */
    function showVariantImagesFromSelectedVariant() {
      if (!productMediaImages) return;
      if (!productHasColorImages) return;
      let variantColour = getVariantColor();
      let variantType = getVariantType();

      if (!variantColour) variantColour = ''; //This way we can check if '' === ''.
      if (!variantType) variantType = ''; //This way we can check if '' === ''.
      if (currentColor === variantColour && currentType === variantType) return; //Don't update if it's the same
      currentColor = variantColour;
      currentType = variantType;
      const imagesForThisColour: ProductMedia[] = productMediaImages.filter(
        (media) => {
          if (!media.alt) return false;
          let mainAlt = media.alt.split('|');
          let mediaAltarray = mainAlt[0].split(',');
          if (handlize(mediaAltarray[0]) === variantColour?.toLowerCase()) {
            if (mediaAltarray[1] === undefined) {
              return true;
            }
            if (handlize(mediaAltarray[1]) === variantType?.toLowerCase()) {
              return true;
            }
          }
        }
      );

      rebuildCarousel(imagesForThisColour);
    }

    function getVariantTypes() {
      if (!productMediaImages) return;
      if (!productHasColorImages) return;
      let variantColour = getVariantColor();
      let variantType = getVariantType();
      if (!variantColour) variantColour = ''; //This way we can check if '' === ''.
      if (!variantType) variantType = ''; //This way we can check if '' === ''.
      if (currentColor === variantColour && currentType === variantType) return; //Don't update if it's the same
      currentColor = variantColour;
      currentType = variantType;
      const imagesForThisColour: ProductMedia[] = productMediaImages.filter(
        (media) => {
          if (!media.alt) return false;

          let mainAlt = media.alt.split('|');
          let mediaAltarray = mainAlt[0].split(',');
          if (handlize(mediaAltarray[0]) === variantColour?.toLowerCase()) {
            if (mediaAltarray[1] === undefined) {
              return true;
            }
            if (handlize(mediaAltarray[1]) === variantType?.toLowerCase()) {
              return true;
            }
          }
        }
      );
      return imagesForThisColour;
    }

    function getVariantColor() {
      const selectedOptionElem = template!.querySelector<HTMLSelectElement>(
        SELECTOR_VARIANT_SELECTOR
      )?.selectedOptions?.[0];
      return selectedOptionElem?.getAttribute(
        `data-option${colourOptionPosition}`
      );
    }

    function getVariantType() {
      const selectedOptionElem = template!.querySelector<HTMLSelectElement>(
        SELECTOR_VARIANT_SELECTOR
      )?.selectedOptions?.[0];
      return selectedOptionElem?.getAttribute(
        `data-option${typeOptionPosition}`
      );
    }

    function rebuildCarousel(images?: ProductMedia[]) {
      if (!carousel || !carouselElement) return;
      carousel.slider.destroy();

      if (!images || !images.length) images = productMediaImages;

      let dots = '';
      let slides = '';

      const product_tile_badge = container.getAttribute(
        'data-product-tile-badge'
      );
      let show_product_tile_badge: boolean = true;

      images.forEach((image: ProductMedia, i) => {
        let mainAlt = image.alt?.split('|');
        let caption: string | null = null;
        if (mainAlt && mainAlt?.length > 1) {
          caption = mainAlt[1].trim();
        }

        dots = `${dots}
        <button
          class="c-product-gallery__dot"
          data-carousel-dot
          data-index="${i}"
          type="button"
          title="Carousel navigation"
        >
          <div class="c-product-gallery__dot-inner"></div>
        </button>
      `;

        slides = `${slides}
        <div
          class="c-product-gallery__slide ${
            image.aspect_ratio > 1.25 ? 'is-landscape' : ''
          }"
          data-product-gallery-slide
          data-index="${i}"
          data-media-id="${image.id}"
        >
          <div
            class="c-product-gallery__slide-image-container o-responsive-image-container"
            data-modal-open 
            data-image-index="${i}"
          >
          <picture>
            <source media="(max-width:300px)" src="${image.src}_374x.jpg">
            <source media="(max-width:750px)" src="${image.src}_750x.jpg">
            <source media="(max-width:400px)" src="${image.src}_1100x.jpg">
            <source media="(min-width:520px)" src="${image.src}_1650x.jpg">
            <source media="(max-width:520px)" src="${image.src}_1650x.jpg">
            <img src="${
              image.src
            }_850x.jpg" class="c-product-gallery__slide-image o-responsive-image">
          </picture>
            ${
              caption !== null
                ? `<div class="image_overlay">
                ${caption}
              </div>`
                : ''
            }
            ${
              show_product_tile_badge == true && product_tile_badge !== ''
                ? `<div class="o-product-thumbnail__badge">
                ${product_tile_badge}
              </div>`
                : ''
            }
          </div>
        </div>
      `;

        show_product_tile_badge = false;
      });
      if (dotsContainer) {
        dotsContainer.innerHTML = dots;
        if (images.length > DOT_LIMIT) {
          dotsContainer.classList.add(CLASS_IS_SLIDER);
          dotsContainer.style.transform = 'translateX(0)';
        } else {
          dotsContainer.classList.remove(CLASS_IS_SLIDER);
        }
      }

      carouselElement.innerHTML = slides;
      carousel = initCarousel(params);
      rebuildLightBox(images);
    }

    function mediaHasColorValues() {
      if (!productMediaImages || !productObj) return false;
      const colourOptionValues = productObj.variants.reduce(
        (x: string[], variant: Variant) => {
          variant.options.forEach((option: string) => {
            if (x.indexOf(option) === -1) x.push(option);
          });
          return x;
        },
        []
      );
      return productMediaImages.some(
        (image: ProductMedia) =>
          image.alt && colourOptionValues.includes(image.alt.split(',')[0])
      );
    }

    /*
     * Hover Zoom
     * Hover Zoom functionality on the PDP page
     * V1.0 29/11/2021
     */
    function intialiseHover() {
      if (responsiveMediaAbove('MEDIUM')) {
        const imageElements =
          document.querySelectorAll<HTMLElement>(SELECTOR_HOVERZOOM);
        if (!imageElements) return;
        imageElements.forEach((imageElement) => {
          if (!imageElement) return;
          imageElement.addEventListener('mouseover', () =>
            mouseOver(imageElement)
          );
          imageElement.addEventListener('mouseout', () =>
            imageZoomOut(imageElement)
          );
        });
      }
    }

    function removeHover(element: HTMLElement) {
      element.style.transform = 'none';
      element.style.left = 'unset';
      element.style.top = 'unset';
      element.removeAttribute(HOVERZOOM_ATTR);
      element.replaceWith(element.cloneNode(true));
    }

    function mouseOver(imageElement: HTMLElement) {
      imageElement.style.transform = 'scale(2,2)';
      imageElement.style.top = '50%';
      imageElement.style.left = '50%';
      imageElement.addEventListener('mousemove', (z) =>
        imageZoomIn(z, imageElement)
      );
    }

    //handles zoom in function calculations
    function imageZoomIn(event: MouseEvent, img: HTMLElement) {
      let posX = event.offsetX - img.offsetWidth / 2;
      let posY = event.offsetY - img.offsetHeight / 2;
      img.style.top = `${-posY}px`;
      img.style.left = `${-posX}px`;
    }

    //handles zoom out functions
    function imageZoomOut(img: HTMLElement) {
      img.style.transform = 'scale(1,1)';
      img.style.top = `0`;
      img.style.left = `0`;
    }

    /*
     * Custom LightBox using modal and carousel to provide Enhanced zoom for
     * the PDP page
     * V1.0 13/01/2022
     */

    //Initialises the lightbox when the page is first loaded.
    function intialiseLightbox() {
      if (responsiveMediaAbove('SMALL')) {
        const images = getVariantTypes();

        document
          .querySelectorAll<HTMLDivElement>(SELECTOR_MODAL_OPEN)
          .forEach((modalImage) => {
            const imageId = modalImage.dataset.imageIndex;
            if (!imageId) return;
            modalImage.addEventListener('click', () =>
              populateLightBox(imageId, images)
            );
          });
      }

      //move zoom button event listener here so its only created once.
      function onResize() {
        if (responsiveMediaBelow('SMALL')) {
          closeModal(MODAL_ID);
          return;
        }
      }
      window.addEventListener('resize', onResize);
    }

    //Populates the Light Box and opens the lightbox
    function populateLightBox(imageId: string, images?: ProductMedia[]) {
      if (responsiveMediaBelow('SMALL')) {
        closeModal(MODAL_ID);
        return;
      }

      const modal = document.querySelector<HTMLDivElement>(SELECTOR_LIGHTBOX);
      if (!container) return;

      const modalBody =
        container.querySelector<HTMLDivElement>(SELECTOR_MODAL_OBJ);
      const lightboxGallery =
        container.querySelector<HTMLDivElement>(SELECTOR_LB_CAROUSEL);

      if (!modalBody || !images || !lightboxGallery || !modal) return;
      if (lightboxGallery.children.length === 0 || modal.children.length == 0) {
        const slides = images.reduce((slides, image) => {
          return `${slides}
          <div
            class="c-product-gallery__zoom-slide"
          >
            <div
              class="c-product-gallery__zoom-container o-responsive-image-container"
            >
            <picture>
              <source media="(max-width:300px)" src="${image.src}_374x.jpg">
              <source media="(max-width:750px)" src="${image.src}_750x.jpg">
              <source media="(max-width:400px)" src="${image.src}_1100x.jpg">
              <source media="(min-width:520px)" src="${image.src}_1650x.jpg">
              <source media="(max-width:520px)" src="${image.src}_1650x.jpg">
              <img src="${image.src}_850x.jpg" class="c-product-gallery__zoom-image o-responsive-image">
            </picture>
            </div>
          </div>
        `;
        }, '');

        lightboxGallery.innerHTML = slides;

        if (!modal) return;
        const modalContent =
          modal?.querySelector<HTMLDivElement>(SELECTOR_MODAL_OBJ);

        if (modalContent && modalBody) {
          modalContent.innerHTML = modalBody.innerHTML;
        }
      }

      if (responsiveMediaBelow('MEDIUM')) {
        openModal({
          modalContentName: MODAL_ID,
          maxWidth: 600,
          uniqueModalElement: modalBody || null,
          globalCloseCross: true || null,
          darkerBackground: true || null,
        });
      } else {
        openModal({
          modalContentName: MODAL_ID,
          maxWidth: 816,
          uniqueModalElement: modalBody || null,
          globalCloseCross: true || null,
          darkerBackground: true || null,
        });
      }

      //Intialising carousel and zoom functionality
      const options2: CarouselOptions = {
        slidesPerView: 1,
        spacing: 2,
        initial: parseInt(imageId),
      };

      const carouselElement =
        modal.querySelector<HTMLDivElement>(SELECTOR_LB_CAROUSEL);
      const controls = modal.querySelector<HTMLDivElement>(SELECTOR_CONTROLS);

      if (!carouselElement || !controls) return;

      if (carouselElement.children.length < 2) {
        controls.classList.add(CLASS_HIDDEN);
      } else {
        controls.classList.remove(CLASS_HIDDEN);
      }

      const modalContents =
        modal.querySelector<HTMLDivElement>(SELECTOR_MODAL_OBJ);
      const zoomButton =
        modalContents?.querySelector<HTMLButtonElement>(SELECTOR_LB_ZOOM);

      carouselInitialised = initCarousel({
        container: carouselElement,
        navigationContainer: controls,
        options: options2,
        onSlideChangedCallback: clearHoverZoomCallback,
      });

      const modalContainer = modal.children[0];

      if (!modalContents || !modalContainer) return;
      modalContents.style.padding = '0';

      if (!carouselInitialised) return;

      const carousel = carouselInitialised.slider;
      
      if (!zoomButton) return;

      // if(carousel)
      //adds event listener to the button for the click zoom
      zoomButton?.addEventListener('click', toggleHoverZoom);

      onModalClose(MODAL_ID, () => {
        zoomButton?.removeEventListener('click', toggleHoverZoom);
        clearHoverZoomCallback();
      });

      function toggleHoverZoom() {
        if (!zoomStatus) {
          if (!carouselInitialised) return;
          const slide =
            carouselInitialised.slides[carousel.details().absoluteSlide];

          const image = slide?.querySelector<HTMLImageElement>('img');
          if (!image || !slide) return;
          image.setAttribute(HOVERZOOM_ATTR, '');
          intialiseHover();

          if (!controls) return;
          controls.style.display = 'none';
          if (!zoomButton) return;
          zoomButton?.children[0].classList.add(CLASS_HIDDEN);
          zoomButton?.children[1].classList.remove(CLASS_HIDDEN);
          zoomStatus = true;
        } else {
          if (!carouselInitialised) return;
          let slides = carouselInitialised.slides;
          for (let i = 0; i < slides.length; i++) {
            let img = slides[i].querySelector<HTMLImageElement>('img');
            if (!img) return;
            img.removeAttribute(HOVERZOOM_ATTR);
            removeHover(img);
          }
          if (!controls) return;
          controls.style.display = 'block';
          if (!zoomButton) return;
          zoomButton?.children[0].classList.remove(CLASS_HIDDEN);
          zoomButton?.children[1].classList.add(CLASS_HIDDEN);
          zoomStatus = false;
        }
      }

      function clearHoverZoomCallback() {
        zoomStatus = false;
        if (!zoomButton || !controls || !carouselInitialised) return;

        let slides = carouselInitialised.slides;
        for (let i = 0; i < slides.length; i++) {
          let img = slides[i].querySelector<HTMLImageElement>('img');
          if (!img) return;

          removeHover(img);
        }

        controls.style.display = 'block';
        zoomButton?.children[0].classList.remove(CLASS_HIDDEN);
        zoomButton?.children[1].classList.add(CLASS_HIDDEN);
      }
      carouselInitialised.slider.refresh();
    }

    //Rebuilds the LightBox when the variants are changed
    function rebuildLightBox(images?: ProductMedia[]) {
      if (responsiveMediaAbove('SMALL')) {
        const lightboxGallery =
          container.querySelector<HTMLDivElement>(SELECTOR_LB_CAROUSEL);

        if (!lightboxGallery) return;

        lightboxGallery.innerHTML = '';

        document
          .querySelectorAll<HTMLDivElement>(SELECTOR_MODAL_OPEN)
          .forEach((modalImage) => {
            const imageId = modalImage.dataset.imageIndex;
            if (!imageId) return;
            modalImage.addEventListener('click', () =>
              populateLightBox(imageId, images)
            );
          });
      }

      function onResize() {
        if (responsiveMediaBelow('SMALL')) {
          closeModal(MODAL_ID);
          return;
        }
      }
      window.addEventListener('resize', onResize);
    }
  });
